import { forwardRef, useState } from 'react'
import {
  Select,
  DonutChart,
  HorisontalBarChart,
  LineChart,
  ColumnChart
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { DonutChartData } from '@lmc/ui-kit/src/dataDisplay/Charts/DonutChart/DonutChart'
import { IncidentCard } from './IncidentCard'
import { Col, Row } from 'antd'
import { TimeSeriesOptions, Home, IncidentSeverityValues } from '@lmc/ts-common'
import styled from 'styled-components'
import _, { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useIncidentsReportList } from '../../hooks'
import { FilterValues } from '../../hooks/useIncidentsReportList'
import { Link } from 'react-router-dom'
import { HorizontalBarChartData } from '@lmc/ui-kit/src/dataDisplay/Charts/HorisontalBarChart/HorisontalBarChart'
import { useSelector } from 'react-redux'
import { getIncidentSeverityDefaultValue } from 'screens/Logs/LogScreen/components/IncidentLogScreens/utils/incidentSeverityUtils'

type IncidentChartProps = {
  filterParams: FilterValues
  testId?: string
}

export const IncidentsCharts = forwardRef<HTMLDivElement, IncidentChartProps>(
  ({ filterParams, testId = 'incidents-charts' }, ref) => {
    const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

    const [incidentDropdownValue, setIncidentDropdownValue] =
      useState<TimeSeriesOptions>(TimeSeriesOptions.total)

    const { data } = useIncidentsReportList(filterParams, 'dashboard')
    const incidents = data?.data ?? []

    const handleChange = (value: TimeSeriesOptions) => {
      setIncidentDropdownValue(value)
    }

    const home = useSelector(
      (state: { global: { home: Home } }) => state.global.home
    )

    const homeSeverities = fillSeverityMissingKeys(home.incidentSeverity)

    const mappedSeverities = Object.fromEntries(
      Object.entries(homeSeverities ?? {}).map(([key, value]) => {
        return [
          key.toString(),
          value || getIncidentSeverityDefaultValue(parseInt(key, 10), t)
        ]
      })
    )

    const mapSeverities = (data: HorizontalBarChartData[]) => {
      return data.map((item, index) => ({
        ...item,
        name: mappedSeverities[index] ?? item.name
      }))
    }

    const mappedLineChartData = () => {
      return incidentDropdownValue === TimeSeriesOptions.severity
        ? mapSeverities(incidents.timeSeries[incidentDropdownValue].data)
        : incidents.timeSeries[incidentDropdownValue].data
    }

    const incidentsOptions = [
      {
        value: TimeSeriesOptions.total,
        label: t(incidentReportsConstants[TimeSeriesOptions.total])
      },
      {
        value: TimeSeriesOptions.location,
        label: t(incidentReportsConstants[TimeSeriesOptions.location])
      },
      {
        value: TimeSeriesOptions.incidentType,
        label: t(incidentReportsConstants[TimeSeriesOptions.incidentType])
      },
      {
        value: TimeSeriesOptions.severity,
        label: t(incidentReportsConstants[TimeSeriesOptions.severity])
      }
    ]

    return (
      <ChartsContainer ref={ref} data-testid={testId}>
        <TopChartsContainer data-testid={`${testId}-top-charts-container`}>
          <IncidentCardWrapper data-testid={`${testId}-actions-card-wrapper`}>
            <IncidentCardContainer
              data-testid={`${testId}-actions-card-container`}
            >
              <IncidentCard
                data-testid={`${testId}-actions-card`}
                title={t(incidentReportsConstants.ACTIONS)}
                headerAction={
                  <StyledLink
                    to="/reports/incidents/actions"
                    data-testid={`${testId}-view-action-table-link`}
                  >
                    {t(incidentReportsConstants.VIEW_ACTION_TABLE)}
                  </StyledLink>
                }
              >
                <ColumnChart
                  data-testid={`${testId}-actions-column-chart`}
                  data={incidents.actions.data}
                  categories={incidents.actions.categories.map((name: string) =>
                    capitalize(t(name))
                  )}
                />
              </IncidentCard>
            </IncidentCardContainer>
          </IncidentCardWrapper>
          <TopRightContainer data-testid={`${testId}-top-right-container`}>
            <IncidentCard
              data-testid={`${testId}-cases-card`}
              title={t(incidentReportsConstants.CASES)}
            >
              <DonutChart
                data-testid={`${testId}-cases-donut-chart`}
                tooltipLabel="Incidents"
                data={incidents.donutChart.data.map((item: DonutChartData) => ({
                  ...item,
                  name: capitalize(t(item.name))
                }))}
                colors={['#0FCA7A', '#FBC62F', '#F75D5F']}
                displayLegend
              />
            </IncidentCard>
            <IncidentCard
              data-testid={`${testId}-cases-filled-card`}
              title={
                <div data-testid={`${testId}-cases-filled-title`}>
                  {t(incidentReportsConstants.CASES)}
                  <br />
                  {t(incidentReportsConstants.FILLED_OUT)}
                </div>
              }
              subtitle={t(incidentReportsConstants.FILLING_CASE_DETAILS)}
            >
              <CaseContainer data-testid={`${testId}-case-container`}>
                <CaseTypeContainer
                  data-testid={`${testId}-debriefing-container`}
                >
                  <Text data-testid={`${testId}-debriefing-label`}>
                    {t(incidentReportsConstants.DEBRIEFING)}
                  </Text>
                  <PercentageText
                    data-testid={`${testId}-debriefing-percentage`}
                  >
                    {incidents?.casesFilled?.debriefing}%
                  </PercentageText>
                </CaseTypeContainer>
                <CaseTypeContainer
                  data-testid={`${testId}-root-cause-container`}
                >
                  <Text data-testid={`${testId}-root-cause-label`}>
                    {t(incidentReportsConstants.ROOT_CAUSE_PCT)}
                  </Text>
                  <PercentageText
                    data-testid={`${testId}-root-cause-percentage`}
                  >
                    {incidents?.casesFilled?.rootCause}%
                  </PercentageText>
                </CaseTypeContainer>
              </CaseContainer>
            </IncidentCard>
          </TopRightContainer>
        </TopChartsContainer>
        <RowContainer
          gutter={24}
          style={{ minHeight: '450px' }}
          data-testid={`${testId}-line-chart-row`}
        >
          <ColContainer span={24} data-testid={`${testId}-line-chart-col`}>
            <IncidentCard
              data-testid={`${testId}-incidents-time-card`}
              title={t(incidentReportsConstants.INCIDENTS_TIME)}
              headerAction={
                <Select
                  data-testid={`${testId}-time-series-select`}
                  style={{ width: 150 }}
                  onChange={handleChange}
                  options={incidentsOptions}
                  value={incidentDropdownValue}
                />
              }
            >
              <LineChart
                data-testid={`${testId}-incidents-time-line-chart`}
                xAxisLabel={t(incidentReportsConstants.TIME)}
                yAxisLabel={t(incidentReportsConstants.TOTAL_INCIDENTS)}
                startDate={incidents.timeSeries.startDate}
                endDate={incidents.timeSeries.endDate}
                data={_.cloneDeep(mappedLineChartData())}
                colors={[
                  '#46A182',
                  '#004c6d',
                  '#ff8c00',
                  '#7851a9',
                  ' #F75D5F',
                  '#708090'
                ]}
                displayLegend={[
                  TimeSeriesOptions.severity,
                  TimeSeriesOptions.location,
                  TimeSeriesOptions.incidentType
                ].includes(incidentDropdownValue)}
              />
            </IncidentCard>
          </ColContainer>
        </RowContainer>
        <RowContainer
          gutter={24}
          style={{ minHeight: '488px' }}
          data-testid={`${testId}-horizontal-bar-chart-row`}
        >
          <ColContainer
            span={24}
            data-testid={`${testId}-horizontal-bar-chart-col`}
          >
            <IncidentCard
              data-testid={`${testId}-top-incidents-card`}
              title={t(incidentReportsConstants.TOP_INCIDENTS)}
            >
              {incidents?.horizontalBarChart && (
                <HorisontalBarChart
                  data-testid={`${testId}-top-incidents-horizontal-bar-chart`}
                  xAxisLabel={t(incidentReportsConstants.TOTAL_INCIDENTS)}
                  yAxisLabel={t(incidentReportsConstants.TYPE_OF_INCIDENT)}
                  colors={[
                    '#595959',
                    '#1AF2E5',
                    '#F1E85D',
                    '#FEA700',
                    '#FC7411',
                    '#F75D5F'
                  ]}
                  categories={incidents.horizontalBarChart.categories}
                  data={mapSeverities(incidents.horizontalBarChart.data)}
                />
              )}
            </IncidentCard>
          </ColContainer>
        </RowContainer>
      </ChartsContainer>
    )
  }
)

const ChartsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TopChartsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 0;
  gap: 24px;
  width: 100%;
  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    flex-direction: column;
  }
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex: 0 1 auto;
    flex-direction: column;
    gap: 24px;
  }
`

const TopRightContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: row;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex-direction: column;
    `

const ColContainer = styled(Col)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;
  }
`

const RowContainer = styled(Row)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const IncidentCardContainer = styled.div`
  height: 100%;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;
  }
`

const IncidentCardWrapper = styled.div`
  width: 50%;
  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    width: 100%;
  }
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const CaseContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    margin-top: 24px;
  }
`

const PercentageText = styled(Text)`
  font-weight: ${(props) => props.theme.font.weight.bold};
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    font-size: 16px;
  }
`

const CaseTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${({ theme }) => theme.font.weight.medium};
`

function fillSeverityMissingKeys(
  obj?: IncidentSeverityValues,
  start: number = 1,
  end: number = 5
): IncidentSeverityValues {
  const result = {
    1: undefined,
    2: undefined,
    3: undefined,
    4: undefined,
    5: undefined
  } as IncidentSeverityValues

  for (let i = start; i <= end; i += 1) {
    const key = i as keyof IncidentSeverityValues
    result[key] = obj?.[key]
  }

  return result
}
